import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";

interface FaqItem {
  q: string;
  a: React.ReactNode;
}

const faqItems: FaqItem[] = [
  {
    q: "Which formats are supported?",
    a: (
      <>
        HANDICALC® covers common formats such as singles stroke play, singles match play,
        fourball betterball, foursomes, greensomes and team events like Texas Scramble and
        best-of formats. You can see a full list on the{" "}
        <Link to="/formats" className="text-primary-700 hover:text-primary-800 font-medium underline underline-offset-2">Formats</Link>{" "}
        page.
      </>
    ),
  },
  {
    q: "Why is my Playing Handicap different at different courses?",
    a: (
      <>
        Your{" "}
        <strong>Playing Handicap</strong>{" "}
        depends on your HI, the CR, SR and par for the tees you are using, plus the allowance for the format being played. When you move to a course or tee with a different rating or slope, your Playing Handicap can legitimately change even if your HI stays the same.
      </>
    ),
  },
  {
    q: "Can HANDICALC® handle mixed tees or mixed-gender competitions?",
    a: (
      <>
        Yes. HANDICALC® supports mixed tees.
        <p className="mt-3">
          In the calculator you select the course and scoring tee. For each player that is not playing from the scoring tee, click the tee selector to the right of the golfer's details and choose the appropriate tee.
        </p>
        <p className="mt-3">
          HANDICALC® automatically applies the correct{" "}
          <strong>CR</strong> and <strong>SR</strong> for the selected tee, so groups playing from different tees can be entered quickly and accurately.
        </p>
      </>
    ),
  },
  {
    q: "How do I enter a plus HI?",
    a: (
      <>
        If a player has a <strong>plus HI</strong>, it should be entered as a{" "}
        <strong>negative number</strong>. For example, a HI of{" "}
        <strong>+1.8</strong> should be entered as{" "}
        <strong>-1.8</strong>.
        <p className="mt-3">
          This reflects how plus handicaps work under the World Handicap System™ and ensures Course Handicap and Playing Handicap calculations are applied correctly.
        </p>
      </>
    ),
  },
  {
    q: "How are fourball and team allowances handled?",
    a: (
      <>
        For fourball and other team formats, HANDICALC® first works out each player's Course Handicap and then applies the appropriate World Handicap System™ methodology allowance for that format (for example 85% for many fourball betterball competitions). For formats like Texas Scramble, it combines percentages from each player's Course Handicap to create a single team Playing Handicap.
      </>
    ),
  },
  {
    q: "Are the allowances always the same percentages?",
    a: (
      <>
        HANDICALC® uses typical allowance percentages for each format (based on World Handicap System™ methodology), but Committees may set different figures in their Terms of Competition. If your club or organiser specifies a different allowance for a particular event, that should always take precedence.
      </>
    ),
  },
  {
    q: "Can I use HANDICALC® for non-WHS™ regions?",
    a: (
      <>
        HANDICALC® is based on World Handicap System™ methodology logic and terminology. In non-WHS™ regions with similar handicap structures, the approach may still be helpful as a guide, but you must follow any local rules and guidance issued by your national association or club for official events.
      </>
    ),
  },
  {
    q: "Is this an official handicapping product?",
    a: (
      <>
        No. HANDICALC® is an independent tool created to help golfers and organisers apply World Handicap System™ methodology calculations. It does not replace the Rules of Handicapping or decisions made by any national association or WHS™ governing body.
      </>
    ),
  },
  {
    q: "Is HANDICALC® free to use?",
    a: (
      <>
        HANDICALC® is free for <strong>personal, individual use</strong>. Golfers are welcome to use the calculator directly on the HANDICALC® website for their own rounds and casual games.
        <p className="mt-3">
          If you want to <strong>embed HANDICALC®</strong> within another website, app or member system, this requires a <strong>licence</strong>. This includes use by clubs, societies, organisations or commercial sites.
        </p>
      </>
    ),
  },
  {
    q: "Do I need an account or login to use HANDICALC®?",
    a: (
      <>
        You can access and use the calculator without creating an account for basic functionality. If additional features that require sign-in are introduced in future, they will be clearly described along with how any data is used.
      </>
    ),
  },
  {
    q: "Does HANDICALC® store my scores or personal data?",
    a: (
      <>
        HANDICALC® is focused on providing shot allowances rather than storing full scoring histories. Any data that is collected or stored is covered in the site's{" "}
        <Link to="/privacy" className="text-primary-700 hover:text-primary-800 font-medium underline underline-offset-2">Privacy</Link>{" "}
        policy. You should review that policy if you have questions about how information is handled.
      </>
    ),
  },
  {
    q: "Can clubs or societies embed HANDICALC® on their own website?",
    a: (
      <>
        HANDICALC® can be linked to from club and society websites for general use.
        <p className="mt-3">
          <strong>Embedding HANDICALC®</strong> within another website, app or member system requires a <strong>licence</strong>. Licensed versions can be configured to suit your organisation, including:
        </p>
        <ul className="mt-2 space-y-1 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed">
          <li>showing <strong>a single course</strong> (for example, your home course only), or</li>
          <li>limiting the calculator to <strong>a specific set of courses or tees</strong>.</li>
        </ul>
        <p className="mt-3">
          If you're interested in embedding or configuring HANDICALC® for your club, society or organisation, please{" "}
          <Link to="/contact" className="text-primary-700 hover:text-primary-800 font-medium underline underline-offset-2">get in touch</Link>{" "}
          to discuss licensing options.
        </p>
      </>
    ),
  },
  {
    q: "What if my club uses slightly different allowances?",
    a: (
      <>
        Some clubs or national associations adopt alternative allowances or extra guidance for certain formats. HANDICALC® uses common World Handicap System™ methodology allowances, but the Committee's Terms of Competition always override the tool where there is any difference.
      </>
    ),
  },
  {
    q: "How is course and tee data kept up to date?",
    a: (
      <>
        HANDICALC® uses structured course and tee data that is sourced from established golf data providers and maintained datasets. This includes CR, SR, par, tee names and gender where available.
        <p className="mt-3">
          Data is reviewed and refreshed regularly, and updates are applied when changes are identified, such as course re-ratings, new tees, renamed tees or corrections to existing information. HANDICALC® is being trialled with UK data only at present. The rest of the world will follow shortly.
        </p>
        <p className="mt-3">
          While every effort is made to keep the data accurate and current, course details can change at short notice. If you spot something that looks incorrect or out of date, please{" "}
          <Link to="/contact" className="text-primary-700 hover:text-primary-800 font-medium underline underline-offset-2">get in touch</Link>{" "}
          so it can be checked and updated.
        </p>
      </>
    ),
  },
  {
    q: "Who should I contact about an error or idea?",
    a: (
      <>
        If something doesn't look right, or you have an idea for a new format or feature, please use the{" "}
        <Link to="/contact" className="text-primary-700 hover:text-primary-800 font-medium underline underline-offset-2">contact page</Link>{" "}
        to get in touch. Feedback from golfers, clubs and organisers helps make HANDICALC® more accurate and useful.
      </>
    ),
  },
];

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleItem = (idx: number) => {
    setOpenIndex((prev) => (prev === idx ? null : idx));
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: typeof item.a === "string" ? item.a : "See FAQ page for full answer.",
      },
    })),
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      {/* JSON-LD FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-primary-900">
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950 via-primary-900 to-primary-800"></div>
          <div
            className="absolute inset-0 bg-cover bg-center opacity-15"
            style={{
              backgroundImage:
                "url('https://readdy.ai/api/search-image?query=Abstract%20atmospheric%20golf%20course%20landscape%20with%20misty%20fairways%20and%20scattered%20bunkers%20in%20early%20morning%20light%2C%20soft%20focus%20painterly%20style%2C%20deep%20emerald%20green%20and%20warm%20golden%20sand%20colour%20palette%2C%20editorial%20artistic%20golf%20landscape%20suitable%20as%20subtle%20dark%20background%20with%20text%20overlay&width=1600&height=900&seq=faq-hero-bg&orientation=landscape')",
            }}
          ></div>
          <div className="relative mx-auto max-w-content px-4 md:px-6 pt-20 md:pt-28 pb-16 md:pb-24 text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur border border-background-50/20 text-xs font-medium text-accent-300">
              <i className="ri-question-answer-line"></i>
              Got questions?
            </span>
            <h1 className="mt-6 font-heading font-bold text-background-50 text-3xl md:text-5xl lg:text-6xl leading-tight max-w-3xl mx-auto">
              Frequently asked questions
            </h1>
            <p className="mt-5 text-primary-100 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Quick answers to common questions about HANDICALC®, the World Handicap System™, golf shot allowance calculations, mixed tees, format allowances, club licensing and more.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/calculator"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                Try the calculator <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-background-50/10 hover:bg-background-50/20 backdrop-blur border border-background-50/30 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                Contact us
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ List */}
        <section aria-label="Frequently asked questions" className="bg-background-50 py-12 md:py-20">
          <div className="mx-auto max-w-3xl px-4 md:px-6">
            <div className="space-y-3">
              {faqItems.map((item, idx) => {
                const isOpen = openIndex === idx;
                return (
                  <article
                    key={idx}
                    className="rounded-xl border border-background-200 bg-background-50 overflow-hidden transition-colors"
                  >
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-4 px-5 md:px-6 py-4 md:py-5 text-left cursor-pointer hover:bg-background-100/60 transition-colors"
                      onClick={() => toggleItem(idx)}
                      aria-expanded={isOpen}
                    >
                      <h2 className="font-heading font-semibold text-foreground-950 text-sm md:text-base pr-4 leading-snug">
                        {item.q}
                      </h2>
                      <span
                        className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-transform duration-200 ${
                          isOpen
                            ? "bg-primary-600 text-background-50 rotate-180"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        <i className="ri-arrow-down-s-line text-sm"></i>
                      </span>
                    </button>

                    {isOpen && (
                      <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
                        <hr className="border-background-200 mb-4" />
                        <div className="text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                          {item.a}
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Still have questions CTA */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="relative overflow-hidden rounded-3xl">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20golden%20hour%20light%20over%20pristine%20golf%20green%20with%20flagstick%20in%20distance%20and%20soft%20shadows%2C%20tranquil%20evening%20atmosphere%2C%20painterly%20editorial%20style%2C%20rich%20emerald%20green%20and%20warm%20amber%20gold%20colour%20palette%2C%20cinematic%20composition%20suitable%20as%20dark%20background%20with%20text%20contrast&width=1600&height=800&seq=faq-cta-bg&orientation=landscape"
                alt="Golf green at golden hour"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-900/75 to-primary-800/60"></div>
              <div className="relative z-10 px-6 md:px-14 py-14 md:py-20 text-center max-w-3xl mx-auto">
                <h2 className="font-heading font-bold text-background-50 text-3xl md:text-4xl leading-tight">
                  Still have a question?
                </h2>
                <p className="mt-4 text-background-100/90 text-base md:text-lg leading-relaxed">
                  If you could not find what you were looking for, send us a message and we will get back to you. We are always happy to help golfers, clubs and societies.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm md:text-base font-semibold whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Contact us <i className="ri-mail-line"></i>
                  </Link>
                  <Link
                    to="/calculator"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-background-50/10 hover:bg-background-50/20 backdrop-blur border border-background-50/30 text-background-50 text-sm md:text-base font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Open the calculator
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}